import { useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  BookOpen,
  Plus,
  Trash2,
  Loader2,
  ArrowLeft,
  ShieldCheck,
  LogOut,
  Download,
  Upload,
  FileText,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import SiteConfigPanel from "@/components/admin/SiteConfigPanel";
import StatsPanel from "@/components/admin/StatsPanel";
import KeyFieldPanel from "@/components/admin/KeyFieldPanel";
import ImportPanel from "@/components/admin/ImportPanel";
import useAdminData, { PAGE_SIZE } from "@/hooks/useAdminData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DIFFS = ["easy", "normal", "hard"];
const DIFF_LABEL = { easy: "Easy", normal: "Normal", hard: "Hard" };

export default function Admin() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const data = useAdminData();

  return (
    <div className="relative min-h-screen w-full" data-testid="admin-page">
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 md:px-10 py-6 sm:py-10 md:py-14">
        <TopBar
          onHome={() => navigate("/")}
          email={user?.email}
          onLogout={async () => {
            await logout();
            navigate("/admin");
          }}
        />

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <p className="font-sans-ui text-xs uppercase tracking-[0.3em] text-[#EB5E28] font-semibold mb-2">
            Editorial Desk
          </p>
          <h1 className="font-serif-display text-4xl md:text-5xl font-semibold">
            Phrase library
          </h1>
          <p className="font-serif-display italic text-lg text-[#6B6A68] mt-2">
            Curate the volumes. Commission fresh material from Claude or OpenAI.
          </p>
        </motion.div>

        <StatsPanel stats={data.stats} />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 mb-10">
          <AddPhraseForm onAdd={data.addPhrase} />
          <ImportPanel
            modelsCatalog={data.modelsCatalog}
            settings={data.settings}
            onImport={data.importPhrases}
            onSaveDefaults={data.saveDefaults}
          />
        </div>

        <KeyFieldPanel
          settings={data.settings}
          onSave={data.saveKeys}
          onClear={async (which) => {
            const body =
              which === "openai"
                ? { openai_api_key: "" }
                : { anthropic_api_key: "" };
            await data.saveKeys(body);
            toast.success("Key removed");
          }}
        />

        <SiteConfigPanel />

        <CsvPanel
          onExport={data.exportCsv}
          onImport={data.importCsv}
        />

        <LibraryPanel
          phrases={data.phrases}
          loading={data.loadingList}
          filterDiff={data.filterDiff}
          filterSource={data.filterSource}
          setFilterDiff={data.setFilterDiff}
          setFilterSource={data.setFilterSource}
          page={data.page}
          totalPages={data.totalPages}
          totalCount={data.totalCount}
          setPage={data.setPage}
          onDelete={data.deletePhrase}
        />
      </div>
    </div>
  );
}

// ---------- Top bar ----------
function TopBar({ onHome, email, onLogout }) {
  return (
    <div className="flex items-center justify-between flex-wrap gap-3 mb-6 md:mb-10 pb-4 border-b-2 border-[#1A1A1A]">
      <button
        onClick={onHome}
        data-testid="admin-back-home"
        className="flex items-center gap-2 font-sans-ui text-xs uppercase tracking-[0.2em] font-semibold hover:text-[#EB5E28] transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Home
      </button>
      <div className="flex items-center gap-2 sm:gap-3 flex-wrap justify-end">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-[#EB5E28]" />
          <span className="hidden sm:inline font-sans-ui text-[10px] uppercase tracking-[0.3em] text-[#6B6A68]">
            Signed in as
          </span>
          <span
            className="font-serif-display italic text-sm sm:text-base max-w-[180px] sm:max-w-none truncate"
            data-testid="admin-email"
          >
            {email}
          </span>
        </div>
        <button
          onClick={onLogout}
          data-testid="admin-logout-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white text-[#1A1A1A] border-2 border-[#1A1A1A] font-sans-ui font-semibold uppercase tracking-[0.15em] text-[10px] shadow-[2px_2px_0px_#1A1A1A] hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_#1A1A1A] active:translate-y-0 active:shadow-[0px_0px_0px_#1A1A1A] transition-all"
        >
          <LogOut className="w-3.5 h-3.5" />
          Sign out
        </button>
      </div>
    </div>
  );
}

// ---------- Add phrase form (inline — small enough) ----------
function AddPhraseForm({ onAdd }) {
  const [phrase, setPhrase] = useState("");
  const [diff, setDiff] = useState("");
  const [adding, setAdding] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!phrase.trim()) return;
    setAdding(true);
    try {
      await onAdd(phrase, diff);
      toast.success("Phrase added");
      setPhrase("");
      setDiff("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add phrase");
    } finally {
      setAdding(false);
    }
  };

  return (
    <section className="bg-white border-2 border-[#1A1A1A] shadow-[5px_5px_0px_#1A1A1A] p-6">
      <h2 className="font-serif-display text-2xl font-semibold mb-1">
        Add a phrase
      </h2>
      <p className="font-sans-ui text-sm text-[#6B6A68] mb-5">
        Apostrophes and punctuation are stripped. A-Z and spaces only.
      </p>
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="block font-sans-ui text-[10px] uppercase tracking-[0.24em] font-semibold text-[#6B6A68] mb-1.5">
            Phrase
          </label>
          <input
            type="text"
            value={phrase}
            onChange={(e) => setPhrase(e.target.value)}
            placeholder="e.g. A PENNY FOR YOUR THOUGHTS"
            data-testid="admin-add-phrase-input"
            className="w-full border-2 border-[#1A1A1A] bg-[#F4F1EA] px-3 py-2.5 font-serif-display text-lg uppercase tracking-wide focus:outline-none focus:ring-2 focus:ring-[#EB5E28]"
          />
        </div>
        <div>
          <label className="block font-sans-ui text-[10px] uppercase tracking-[0.24em] font-semibold text-[#6B6A68] mb-1.5">
            Difficulty (optional — auto by word count)
          </label>
          <div className="flex gap-2 flex-wrap">
            {["", ...DIFFS].map((d) => (
              <button
                type="button"
                key={d || "auto"}
                onClick={() => setDiff(d)}
                data-testid={`admin-diff-${d || "auto"}`}
                className={`px-3 py-1.5 font-sans-ui text-xs uppercase tracking-[0.15em] font-semibold border-2 transition-all ${
                  diff === d
                    ? "bg-[#1A1A1A] text-[#F4F1EA] border-[#1A1A1A]"
                    : "bg-white text-[#1A1A1A] border-[#1A1A1A] hover:bg-[#F4F1EA]"
                }`}
              >
                {d ? DIFF_LABEL[d] : "Auto"}
              </button>
            ))}
          </div>
        </div>
        <button
          type="submit"
          disabled={adding || !phrase.trim()}
          data-testid="admin-add-submit"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#EB5E28] text-[#F4F1EA] border-2 border-[#1A1A1A] font-sans-ui font-bold uppercase tracking-[0.15em] text-xs shadow-[3px_3px_0px_#1A1A1A] hover:-translate-y-0.5 hover:shadow-[5px_5px_0px_#1A1A1A] active:translate-y-0 active:shadow-[0px_0px_0px_#1A1A1A] transition-all disabled:opacity-40"
        >
          {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Add phrase
        </button>
      </form>
    </section>
  );
}

// ---------- CSV panel ----------
function CsvPanel({ onExport, onImport }) {
  const fileInputRef = useRef(null);
  const [mode, setMode] = useState("append");
  const [fileName, setFileName] = useState("");
  const [confirmOverwrite, setConfirmOverwrite] = useState(false);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [result, setResult] = useState(null);

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await onExport();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const today = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `fraseology-phrases-${today}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV exported");
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async () => {
    const file = fileInputRef.current?.files?.[0];
    if (!file) {
      toast("Choose a CSV file first");
      return;
    }
    if (mode === "overwrite" && !confirmOverwrite) {
      toast.error("Tick the confirmation box to overwrite the library");
      return;
    }
    setImporting(true);
    setResult(null);
    try {
      const data = await onImport(file, mode);
      setResult(data);
      toast.success(`${data.added} phrases imported`, {
        description:
          mode === "overwrite"
            ? `${data.deleted_on_overwrite} wiped, ${data.skipped_invalid} invalid`
            : `${data.skipped_duplicates} duplicates, ${data.skipped_invalid} invalid`,
      });
      if (fileInputRef.current) fileInputRef.current.value = "";
      setFileName("");
      setConfirmOverwrite(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  return (
    <section
      className="bg-white border-2 border-[#1A1A1A] shadow-[5px_5px_0px_#1A1A1A] p-6 mb-10"
      data-testid="admin-csv-section"
    >
      <div className="flex items-center gap-2 mb-1">
        <FileText className="w-4 h-4 text-[#EB5E28]" />
        <span className="font-sans-ui text-[10px] uppercase tracking-[0.24em] font-bold text-[#EB5E28]">
          Bulk transfer
        </span>
      </div>
      <h2 className="font-serif-display text-2xl font-semibold mb-1">
        Export &amp; import CSV
      </h2>
      <p className="font-sans-ui text-sm text-[#6B6A68] mb-5">
        Columns: <span className="font-mono">phrase</span>,{" "}
        <span className="font-mono">difficulty</span> (optional),{" "}
        <span className="font-mono">source</span> (optional). Apostrophes and
        punctuation are stripped on import.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="border-2 border-[#1A1A1A]/20 p-4">
          <p className="font-sans-ui text-[10px] uppercase tracking-[0.2em] font-semibold text-[#6B6A68] mb-2">
            Export all
          </p>
          <p className="font-sans-ui text-sm text-[#1A1A1A] mb-4">
            Download every stored phrase as a CSV. Portable to PHP/MySQL, Excel, or anywhere.
          </p>
          <button
            onClick={handleExport}
            disabled={exporting}
            data-testid="admin-export-csv"
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#1A1A1A] text-[#F4F1EA] border-2 border-[#1A1A1A] font-sans-ui font-bold uppercase tracking-[0.15em] text-xs shadow-[3px_3px_0px_#EB5E28] hover:-translate-y-0.5 hover:shadow-[5px_5px_0px_#EB5E28] active:translate-y-0 active:shadow-[0px_0px_0px_#EB5E28] transition-all disabled:opacity-40"
          >
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Download CSV
          </button>
        </div>

        <div className="border-2 border-[#1A1A1A]/20 p-4">
          <p className="font-sans-ui text-[10px] uppercase tracking-[0.2em] font-semibold text-[#6B6A68] mb-2">
            Import from file
          </p>
          <div className="space-y-3">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) { setFileName(f.name); setResult(null); }
              }}
              data-testid="admin-import-csv-file"
              className="block w-full font-sans-ui text-sm text-[#1A1A1A] file:mr-3 file:py-2 file:px-3 file:border-2 file:border-[#1A1A1A] file:bg-[#F4F1EA] file:font-sans-ui file:text-[10px] file:uppercase file:tracking-[0.15em] file:font-semibold file:cursor-pointer file:hover:bg-white"
            />
            {fileName && (
              <p className="font-sans-ui text-xs text-[#6B6A68] italic truncate">{fileName}</p>
            )}

            <div>
              <p className="font-sans-ui text-[10px] uppercase tracking-[0.2em] font-semibold text-[#6B6A68] mb-1.5">
                Mode
              </p>
              <div className="flex gap-2 flex-wrap">
                {[
                  { id: "append", label: "Add to library" },
                  { id: "overwrite", label: "Overwrite all" },
                ].map((m) => (
                  <button
                    type="button"
                    key={m.id}
                    onClick={() => { setMode(m.id); setConfirmOverwrite(false); }}
                    data-testid={`admin-import-csv-mode-${m.id}`}
                    className={`px-3 py-1.5 font-sans-ui text-xs uppercase tracking-[0.15em] font-semibold border-2 transition-all ${
                      mode === m.id
                        ? m.id === "overwrite"
                          ? "bg-[#842029] text-[#F4F1EA] border-[#842029]"
                          : "bg-[#1A1A1A] text-[#F4F1EA] border-[#1A1A1A]"
                        : "bg-white text-[#1A1A1A] border-[#1A1A1A] hover:bg-[#F4F1EA]"
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>

            {mode === "overwrite" && (
              <label
                className="flex items-start gap-2 border-2 border-[#842029] bg-[#F8D7DA] p-3 cursor-pointer"
                data-testid="admin-import-csv-confirm-label"
              >
                <input
                  type="checkbox"
                  checked={confirmOverwrite}
                  onChange={(e) => setConfirmOverwrite(e.target.checked)}
                  data-testid="admin-import-csv-confirm"
                  className="mt-0.5 accent-[#842029]"
                />
                <span className="font-sans-ui text-xs text-[#842029] leading-relaxed">
                  <AlertTriangle className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
                  <b>Danger:</b> this deletes every existing phrase before importing. Consider exporting first.
                </span>
              </label>
            )}

            <button
              onClick={handleImport}
              disabled={importing || !fileName || (mode === "overwrite" && !confirmOverwrite)}
              data-testid="admin-import-csv-submit"
              className={`inline-flex items-center gap-2 px-4 py-2.5 border-2 border-[#1A1A1A] font-sans-ui font-bold uppercase tracking-[0.15em] text-xs transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
                mode === "overwrite"
                  ? "bg-[#842029] text-[#F4F1EA] shadow-[3px_3px_0px_#1A1A1A] hover:-translate-y-0.5 hover:shadow-[5px_5px_0px_#1A1A1A] active:translate-y-0 active:shadow-[0px_0px_0px_#1A1A1A]"
                  : "bg-[#EB5E28] text-[#F4F1EA] shadow-[3px_3px_0px_#1A1A1A] hover:-translate-y-0.5 hover:shadow-[5px_5px_0px_#1A1A1A] active:translate-y-0 active:shadow-[0px_0px_0px_#1A1A1A]"
              }`}
            >
              {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {mode === "overwrite" ? "Overwrite & import" : "Import & add"}
            </button>

            {result && (
              <div
                className="mt-1 border-t-2 border-dashed border-[#1A1A1A]/30 pt-3 font-sans-ui text-xs text-[#1A1A1A] space-y-0.5"
                data-testid="admin-csv-result"
              >
                <div>
                  Mode <b>{result.mode}</b> · Read <b>{result.rows_read}</b> · Added{" "}
                  <b className="text-[#0F5132]">{result.added}</b>
                </div>
                <div className="text-[#6B6A68]">
                  Duplicates {result.skipped_duplicates} · Invalid {result.skipped_invalid}
                  {result.mode === "overwrite" ? ` · Wiped ${result.deleted_on_overwrite}` : ""}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------- Library ----------
function LibraryPanel({
  phrases, loading,
  filterDiff, filterSource, setFilterDiff, setFilterSource,
  page, totalPages, totalCount, setPage,
  onDelete,
}) {
  const handleDel = async (id) => {
    try {
      await onDelete(id);
      toast.success("Phrase removed");
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <section>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h2 className="font-serif-display text-2xl font-semibold flex items-center gap-2">
          <BookOpen className="w-5 h-5" />
          Library
        </h2>
        <div className="flex gap-2 flex-wrap">
          {DIFFS.map((d) => (
            <button
              key={d}
              onClick={() => setFilterDiff(d)}
              data-testid={`admin-filter-${d}`}
              className={`px-3 py-1.5 font-sans-ui text-xs uppercase tracking-[0.15em] font-semibold border-2 transition-all ${
                filterDiff === d
                  ? "bg-[#1A1A1A] text-[#F4F1EA] border-[#1A1A1A]"
                  : "bg-white text-[#1A1A1A] border-[#1A1A1A] hover:bg-[#F4F1EA]"
              }`}
            >
              {DIFF_LABEL[d]}
            </button>
          ))}
          <select
            value={filterSource}
            onChange={(e) => setFilterSource(e.target.value)}
            data-testid="admin-filter-source"
            className="px-3 py-1.5 font-sans-ui text-xs uppercase tracking-[0.15em] font-semibold border-2 border-[#1A1A1A] bg-white cursor-pointer"
          >
            <option value="">All sources</option>
            <option value="seed">Seed</option>
            <option value="manual">Manual</option>
            <option value="claude">Claude</option>
            <option value="openai">OpenAI</option>
          </select>
        </div>
      </div>

      <div className="bg-white border-2 border-[#1A1A1A] shadow-[5px_5px_0px_#1A1A1A]">
        {loading ? (
          <div className="p-10 text-center font-serif-display italic text-[#6B6A68]">
            Fetching volumes…
          </div>
        ) : phrases.length === 0 ? (
          <div className="p-10 text-center font-serif-display italic text-[#6B6A68]">
            No phrases match this filter.
          </div>
        ) : (
          <ul className="divide-y-2 divide-[#1A1A1A]/10" data-testid="admin-phrase-list">
            {phrases.map((p, i) => (
              <li
                key={p.id}
                className="flex items-center justify-between gap-3 px-4 md:px-5 py-3 hover:bg-[#F4F1EA]/70 transition-colors"
              >
                <div className="flex items-center gap-4 min-w-0">
                  <span className="font-sans-ui text-[10px] uppercase tracking-[0.2em] text-[#6B6A68] font-semibold w-10 shrink-0 tabular-nums">
                    {String((page - 1) * PAGE_SIZE + i + 1).padStart(3, "0")}
                  </span>
                  <span className="font-serif-display text-lg md:text-xl truncate">
                    {p.phrase}
                  </span>
                  <SourceBadge source={p.source} />
                </div>
                <button
                  onClick={() => handleDel(p.id)}
                  data-testid={`admin-delete-${p.id}`}
                  className="p-2 text-[#6B6A68] hover:text-[#842029] hover:bg-[#F8D7DA]/50 rounded-sm transition-colors"
                  title="Delete phrase"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <p className="font-sans-ui text-xs text-[#6B6A68] mt-3 italic">
        Showing page {page} of {totalPages} · {totalCount} phrase
        {totalCount === 1 ? "" : "s"} match this filter
        {loading ? " · loading…" : ""}.
      </p>

      {totalPages > 1 && (
        <Pagination page={page} totalPages={totalPages} onChange={setPage} />
      )}
    </section>
  );
}

function SourceBadge({ source }) {
  const map = {
    seed: { bg: "bg-[#F4F1EA]", text: "text-[#1A1A1A]", label: "Seed" },
    manual: { bg: "bg-[#D1E7DD]", text: "text-[#0F5132]", label: "Manual" },
    claude: { bg: "bg-[#EB5E28]/15", text: "text-[#EB5E28]", label: "Claude" },
    openai: { bg: "bg-[#D0E7F8]", text: "text-[#134266]", label: "OpenAI" },
  };
  const s = map[source] || map.seed;
  return (
    <span
      className={`${s.bg} ${s.text} font-sans-ui text-[9px] uppercase tracking-[0.22em] font-bold px-2 py-0.5 border border-current/20 shrink-0`}
    >
      {s.label}
    </span>
  );
}

// ---------- Pagination ----------
function buildPageItems(current, total) {
  const items = new Set([1, total, current, current - 1, current + 1, 2, total - 1]);
  const pages = [...items].filter((n) => n >= 1 && n <= total).sort((a, b) => a - b);
  const out = [];
  for (let i = 0; i < pages.length; i++) {
    out.push(pages[i]);
    if (i < pages.length - 1 && pages[i + 1] - pages[i] > 1) out.push("…");
  }
  return out;
}

function Pagination({ page, totalPages, onChange }) {
  const items = buildPageItems(page, totalPages);
  const go = (p) => {
    if (p < 1 || p > totalPages || p === page) return;
    onChange(p);
  };
  const btnBase =
    "inline-flex items-center justify-center min-w-[36px] h-9 px-2 font-sans-ui text-xs font-semibold border-2 transition-all select-none";
  return (
    <nav
      className="mt-5 flex items-center justify-center flex-wrap gap-1.5"
      aria-label="Pagination"
      data-testid="phrase-library-pagination"
    >
      <button onClick={() => go(1)} disabled={page === 1} aria-label="First page" data-testid="pagination-first"
        className={`${btnBase} bg-white text-[#1A1A1A] border-[#1A1A1A] hover:bg-[#F4F1EA] disabled:opacity-30 disabled:cursor-not-allowed`}>
        <ChevronsLeft className="w-4 h-4" />
      </button>
      <button onClick={() => go(page - 1)} disabled={page === 1} aria-label="Previous page" data-testid="pagination-prev"
        className={`${btnBase} bg-white text-[#1A1A1A] border-[#1A1A1A] hover:bg-[#F4F1EA] disabled:opacity-30 disabled:cursor-not-allowed`}>
        <ChevronLeft className="w-4 h-4" />
      </button>
      {items.map((it, idx) =>
        it === "…" ? (
          <span key={`gap-${idx}`} className="inline-flex items-center justify-center min-w-[28px] h-9 font-sans-ui text-xs text-[#6B6A68]">…</span>
        ) : (
          <button
            key={it}
            onClick={() => go(it)}
            data-testid={`pagination-page-${it}`}
            aria-current={it === page ? "page" : undefined}
            className={`${btnBase} tabular-nums ${
              it === page
                ? "bg-[#1A1A1A] text-[#F4F1EA] border-[#1A1A1A] shadow-[2px_2px_0px_#EB5E28]"
                : "bg-white text-[#1A1A1A] border-[#1A1A1A] hover:bg-[#F4F1EA]"
            }`}
          >
            {it}
          </button>
        )
      )}
      <button onClick={() => go(page + 1)} disabled={page === totalPages} aria-label="Next page" data-testid="pagination-next"
        className={`${btnBase} bg-white text-[#1A1A1A] border-[#1A1A1A] hover:bg-[#F4F1EA] disabled:opacity-30 disabled:cursor-not-allowed`}>
        <ChevronRight className="w-4 h-4" />
      </button>
      <button onClick={() => go(totalPages)} disabled={page === totalPages} aria-label="Last page" data-testid="pagination-last"
        className={`${btnBase} bg-white text-[#1A1A1A] border-[#1A1A1A] hover:bg-[#F4F1EA] disabled:opacity-30 disabled:cursor-not-allowed`}>
        <ChevronsRight className="w-4 h-4" />
      </button>
    </nav>
  );
}
